import React, { useCallback, useRef, useState } from 'react';
import { ArrowLeft, ArrowUp } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useProjectPanelStore } from '../../store/projectPanelStore';
import { AssetType } from '../../../core/types/asset.types';
import { SlotCoreStatusBadge } from '../slot-core/SlotCoreStatusBadge';

const FILTER_OPTIONS: { type: AssetType | 'all'; label: string; icon: string }[] = [
  { type: 'all', label: 'All Assets', icon: '📋' },
  { type: 'sprite', label: 'Sprites', icon: '🖼️' },
  { type: 'audio-music', label: 'Music', icon: '🎵' },
  { type: 'audio-sfx', label: 'Sound Effects', icon: '🔊' },
  { type: 'script-ts', label: 'Scripts', icon: '📜' },
  { type: 'prefab', label: 'Prefabs', icon: '📦' },
  { type: 'scene', label: 'Scenes', icon: '🎬' },
];

export const ProjectToolbar: React.FC = () => {
  const { viewMode, setViewMode, filter, setSearch, setTypeFilter, setSortBy, toggleSortDirection, refreshAssets, isRefreshing, selectedFolderId, selectFolder, folders } = useProjectPanelStore();
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const historyRef = useRef<string[]>([]);
  const historyIndexRef = useRef(-1);

  // Track folder navigation history
  const navigateToFolder = useCallback((folderId: string) => {
    // Trim forward history
    historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
    historyRef.current.push(folderId);
    historyIndexRef.current = historyRef.current.length - 1;
    selectFolder(folderId);
  }, [selectFolder]);

  const canGoBack = historyIndexRef.current > 0;
  const goBack = useCallback(() => {
    if (historyIndexRef.current > 0) {
      historyIndexRef.current--;
      selectFolder(historyRef.current[historyIndexRef.current]);
    }
  }, [selectFolder]);

  const currentFolder = folders[selectedFolderId];
  const canGoUp = !!currentFolder?.parentId;
  const goUp = useCallback(() => {
    if (currentFolder?.parentId) {
      navigateToFolder(currentFolder.parentId);
    }
  }, [currentFolder, navigateToFolder]);

  const handleTypeFilter = useCallback((type: AssetType | 'all') => {
    if (type === 'all') setTypeFilter([]);
    else if (type === 'script-ts') setTypeFilter(['script-ts', 'script-js']);
    else setTypeFilter([type]);
    setShowFilterDropdown(false);
  }, [setTypeFilter]);

  const activeFilterLabel = filter.types.length === 0 ? 'All' : FILTER_OPTIONS.find((f) => filter.types.includes(f.type as AssetType))?.label || 'Filtered';

  return (
    <div className="flex items-center gap-2 px-2 py-1.5 bg-zinc-800 border-b border-zinc-700">
      {/* Back & Up buttons */}
      <button
        onClick={goBack}
        disabled={!canGoBack}
        className={cn(
          'p-1 rounded text-zinc-400',
          canGoBack ? 'hover:bg-zinc-700 hover:text-zinc-200' : 'opacity-30 cursor-default'
        )}
        title="Back (Alt+Left)"
      >
        <ArrowLeft size={14} />
      </button>
      <button
        onClick={goUp}
        disabled={!canGoUp}
        className={cn(
          'p-1 rounded text-zinc-400',
          canGoUp ? 'hover:bg-zinc-700 hover:text-zinc-200' : 'opacity-30 cursor-default'
        )}
        title="Up to parent folder (Alt+Up)"
      >
        <ArrowUp size={14} />
      </button>
      <div className="relative flex-1 max-w-xs">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500 text-xs">🔍</span>
        <input
          type="text"
          value={filter.search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search assets..."
          className="w-full pl-7 pr-2 py-1 text-xs rounded bg-zinc-700 text-zinc-300 placeholder-zinc-500 border border-transparent focus:border-blue-500 focus:outline-none"
        />
      </div>

      <div className="relative">
        <button onClick={() => setShowFilterDropdown(!showFilterDropdown)} className={cn('flex items-center gap-1 px-2 py-1 text-xs rounded bg-zinc-700 text-zinc-300 hover:bg-zinc-600', filter.types.length > 0 && 'text-blue-400')}>
          ⚙️ {activeFilterLabel} ▼
        </button>
        {showFilterDropdown && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setShowFilterDropdown(false)} />
            <div className="absolute top-full left-0 mt-1 z-50 bg-zinc-800 border border-zinc-600 rounded shadow-lg min-w-[150px]">
              {FILTER_OPTIONS.map((opt) => (
                <button key={opt.type} onClick={() => handleTypeFilter(opt.type)} className={cn('w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-blue-600', (opt.type === 'all' && filter.types.length === 0) || filter.types.includes(opt.type as AssetType) ? 'text-blue-400' : 'text-zinc-300')}>
                  {opt.icon} {opt.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="relative">
        <button onClick={() => setShowSortDropdown(!showSortDropdown)} className="flex items-center gap-1 px-2 py-1 text-xs rounded bg-zinc-700 text-zinc-300 hover:bg-zinc-600">
          Sort: {filter.sortBy}
          <span onClick={(e) => { e.stopPropagation(); toggleSortDirection(); }} className="hover:text-blue-400">{filter.sortDirection === 'asc' ? '↑' : '↓'}</span>
        </button>
        {showSortDropdown && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setShowSortDropdown(false)} />
            <div className="absolute top-full left-0 mt-1 z-50 bg-zinc-800 border border-zinc-600 rounded shadow-lg min-w-[120px]">
              {['name', 'type', 'size', 'modified'].map((s) => (
                <button key={s} onClick={() => { setSortBy(s as any); setShowSortDropdown(false); }} className={cn('w-full px-3 py-1.5 text-xs text-left hover:bg-blue-600', filter.sortBy === s ? 'text-blue-400' : 'text-zinc-300')}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="flex-1" />

      <div className="flex items-center bg-zinc-700 rounded overflow-hidden">
        <button onClick={() => setViewMode('list')} className={cn('px-2 py-1 text-xs', viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-zinc-300 hover:bg-zinc-600')} title="List View">☰</button>
        <button onClick={() => setViewMode('grid')} className={cn('px-2 py-1 text-xs', viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-zinc-300 hover:bg-zinc-600')} title="Grid View">⊞</button>
      </div>

      <button
        onClick={refreshAssets}
        disabled={isRefreshing}
        className={cn(
          'px-2 py-1 text-xs rounded bg-zinc-700 text-zinc-300',
          isRefreshing ? 'opacity-50 cursor-not-allowed' : 'hover:bg-zinc-600'
        )}
        title="Refresh"
      >
        <span className={isRefreshing ? 'animate-spin inline-block' : ''}>↻</span>
      </button>

      <SlotCoreStatusBadge />
    </div>
  );
};
